import { Entity, mapEntity } from '@api';
import { formatPriceDisplay } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import { mapEntityToPriceEntity, useLivePrice } from '@terminal/hooks/use-live-price';
import { usePreliminaryPrice } from '@terminal/react-query-hooks/prices/usePreliminaryPrice';
import { useSnakeMeta } from '@terminal/react-query-hooks/snakes/useSnake';
import { getPriceData } from '@terminal/widgets/asset-overview-widget/utils/asset-box/asset-box-helpers';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './company-card/CompanyCard.module.scss';
import { isReferenceDateOlderThanTwoDays } from './utils';

export interface PriceChangeProps {
  companyData: Entity;
  showPostfix?: boolean;
  referenceDate?: string;
}

export const PriceChange = ({
  companyData,
  showPostfix = true,
  referenceDate,
}: PriceChangeProps) => {
  const { t } = useTranslation(['earnings']);

  const mappedEntity = mapEntity(companyData);
  const { data: snakeMeta } = useSnakeMeta(companyData?.default_snake);
  const { data: preliminaryPrice } = usePreliminaryPrice(
    companyData.subscribable_ticker ?? ''
  );

  const priceEntities = mapEntityToPriceEntity(companyData, snakeMeta);
  const shouldDisableLive = isReferenceDateOlderThanTwoDays(referenceDate);

  const livePriceData = useLivePrice(shouldDisableLive ? [] : priceEntities);
  const livePriceInfo = getPriceData({
    hoverData: undefined,
    livePriceData,
    entity: mappedEntity,
    ts: [],
    priceEntity: priceEntities[0],
  });
  const price = formatPriceDisplay({
    exchange: companyData.exchange.code,
    livePriceInfo,
    preliminaryPrice: preliminaryPrice?.data,
    meta: snakeMeta,
    isHovered: false,
    t,
  });

  if (!livePriceInfo || !price.primary.priceChange) {
    return null;
  }

  const hasLivePrice = !!price.primary?.isLive || !!price.secondary?.isLive;
  const priceChange = price.secondary?.priceChange
    ? price.secondary.priceChange
    : price.primary?.priceChange;
  const outsideHoursTag =
    price.secondary?.isLive && price.secondary?.outsideHours?.tag
      ? price.secondary.outsideHours.tag
      : 'open';

  return (
    <span className={styles.priceChangeWrapper}>
      <span
        className={clsx(styles.priceChangeContainer, {
          [styles.priceChangeContainerPositive]:
            priceChange.status === 'positive',
          [styles.priceChangeContainerNegative]:
            priceChange.status === 'negative',
          [styles.priceChangeContainerDefault]:
            priceChange.status === 'default',
        })}
      >
        {priceChange.proportionChange}
      </span>
      {showPostfix && (
        <span
          className={clsx(styles.dateLabel, {
            [styles[outsideHoursTag]]: hasLivePrice,
          })}
        >
          {t(
            hasLivePrice
              ? `earningsInsight.drawer.earningsRecap.liveData`
              : `earningsInsight.drawer.earningsRecap.asOfLastClose`
          )}
        </span>
      )}
    </span>
  );
};
